<template>
	<span>
		<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
			<template #activator="{ props }">
				<slot name="button" :props="props">
					<v-icon
						v-if="!hasCustomButton()"
						v-test="'contacts-filter'"
						class="icon-filter-solid status-label-filter"
						v-bind="props"></v-icon>
				</slot>
			</template>
			<v-card class="menu-card search-filter" rounded="lg" elevation="8" @click.stop>
				<v-card-text class="pb-0">
					<v-text-field
						v-model="searchQuery"
						label="Search"
						append-inner-icon="icon-search-magnifying-glass"
						single-line
						variant="outlined"
						v-bind="searchFieldProps(searchQuery)">
					</v-text-field>
				</v-card-text>
				<FilterMenuList
					ref="containerRef"
					:items="sortedCustomers"
					:loading="loading"
					:empty-text="searchQuery.trim() ? 'No contacts match your search' : 'No contacts available'"
					:is-selected="isSelected"
					loading-test-id="customer-filter-loading"
					item-class="w-100 customer-list-item"
					:row-attrs="(customer) => ({ 'data-id': customer.id })"
					@toggle="toggleSelected">
					<template #skeleton>
						<v-skeleton-loader type="list-item-two-line" class="customer-skeleton-row"></v-skeleton-loader>
					</template>
					<template #item="{ item: customer }">
						<v-list-item-title>{{ getcustomerViewDisplayNameWrapper(customer) }}</v-list-item-title>
						<!-- LSP-13672: a name-only row makes two same-named contacts indistinguishable. Show phone and
							email beneath the name, but only when the title is not already that value — the title falls
							back name > emails > phones (getCustomerViewDisplayName in helpers/utilities.js). -->
						<v-list-item-subtitle v-if="shouldShowPhoneSubtitle(customer)" v-test="'customer-filter-phone'">
							{{ getFormattedPhoneSubtitle(customer) }}
						</v-list-item-subtitle>
						<v-list-item-subtitle v-if="shouldShowEmailSubtitle(customer)" v-test="'customer-filter-email'">
							{{ getEmailSubtitleText(customer) }}
						</v-list-item-subtitle>
					</template>
				</FilterMenuList>
				<div class="opacity-50">
					<hr />
				</div>
				<v-card-actions class="d-flex justify-end">
					<v-btn
						color="grey"
						text
						rounded="xl"
						elevation="0"
						class="white-bg"
						@click="clearFromUser(true)"
						>Clear</v-btn
					>
					<v-btn
						color="primary"
						rounded="xl"
						elevation="0"
						variant="flat"
						@click="applyFromUser()"
						>Apply</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-menu>
	</span>
</template>

<script setup lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { ref, computed, watch, onMounted, onBeforeUnmount, useSlots } from "vue";
	import { getCustomerViewDisplayName } from "../../helpers/utilities";
	import { sortCustomersBySelected } from "../../helpers/filters";
	import { useFilterMenu } from "../../composables/useFilterMenu";
	import { useOrganization } from "../../composables/useOrganization";
	import { formatPhones } from "../../helpers/phone";
	import api from "../../api";
	import { Customer } from "../../types/Customer";
	import ResponseArray from "../../types/ResponseArray";
	import { logError } from "../../helpers/logError";
	import FilterMenuList from "./shared/FilterMenuList.vue";

	const { currentOrganizationDefaultCountry } = useOrganization();

	const props = withDefaults(
		defineProps<{
			headerText?: string;
			preSelectedCustomers?: string[];
		}>(),
		{
			headerText: "Filter by customer",
			preSelectedCustomers: () => [] as string[]
		}
	);

	const emit = defineEmits<{
		(e: "filter-customer", customers: Customer[]): void;
	}>();

	// Backend's fastifyPaging middleware caps each request at 1000 rows. We loop in
	// 1000-row chunks up to MAX_CUSTOMERS so orgs with >1k contacts can filter by every
	// contact, not just the first page.
	const PAGE_CHUNK = 1000;
	const MAX_CUSTOMERS = 50000;

	const searchQuery = ref<string>("");
	const customers = ref<Customer[]>([]);
	const loading = ref<boolean>(false);
	const slots = useSlots();
	const hasCustomButton = (): boolean => slots.button !== undefined;
	let activeSearchRequestId = 0;
	let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	// LSP-16409: every explicit user action (clear/apply) bumps this generation. Async
	// prefill paths (onMount/watcher/restore) capture it before awaiting and bail out if it
	// changed — otherwise a prefill that resolves AFTER the user removed the filter silently
	// re-applies (resurrects) the cleared filter.
	let userActionGeneration = 0;

	const { isFilterOpen, selectedItems, appliedItems, containerRef, isSelected, toggleSelected, applyFilter, clear } =
		useFilterMenu<Customer>(
			async () => {
				await loadCustomers(props.preSelectedCustomers);
				if (appliedItems.value.length === 0 && props.preSelectedCustomers?.length > 0) {
					selectedItems.value = customers.value.filter((c) => props.preSelectedCustomers.includes(c.id));
				}
			},
			() => {
				searchQuery.value = "";
			}
		);

	// Computed
	const sortedCustomers = computed((): Customer[] => {
		return sortCustomersBySelected(customers.value, props.preSelectedCustomers);
	});

	// Methods
	function clearFromUser(emitEvent: boolean): void {
		userActionGeneration += 1;
		clear(() => emit("filter-customer", []), emitEvent);
	}

	function applyFromUser(): void {
		userActionGeneration += 1;
		applyFilter(() => emit("filter-customer", [...appliedItems.value]));
	}

	/**
	 * Returns true (and logs) when a user clear/apply happened while an async prefill was
	 * awaiting, meaning the prefill result is stale and must not be applied (LSP-16409).
	 */
	function prefillSuperseded(generationAtStart: number, source: string): boolean {
		if (generationAtStart === userActionGeneration) {
			return false;
		}
		console.warn(`CustomerFilter.${source}: prefill superseded by a user filter action while loading — skipping stale re-apply`, {
			generationAtStart,
			userActionGeneration,
			preSelectedCustomers: props.preSelectedCustomers?.length ?? 0,
			appliedItems: appliedItems.value.length
		});
		return true;
	}

	const onMount = async (): Promise<void> => {
		if (props.preSelectedCustomers?.length > 0) {
			const generationAtStart = userActionGeneration;
			const prefilled = await setPreselectedCustomers(props.preSelectedCustomers);
			if (prefillSuperseded(generationAtStart, "onMount")) {
				return;
			}
			selectedItems.value = prefilled;
			applyFilter(() => emit("filter-customer", appliedItems.value), false);
		} else {
			selectedItems.value = [];
		}
	};

	async function loadCustomers(preSelectedCustomers: string[] = []): Promise<void> {
		activeSearchRequestId += 1;
		const requestId = activeSearchRequestId;
		if (exceedsSearchLimit(searchQuery.value)) {
			loading.value = false;
			return;
		}

		loading.value = true;
		// TODO: this should be moved into the api.js file, it exists in at least 2 places now
		const searchQueryTrimmed = searchQuery.value.trim();
		let where: any = {};

		if (searchQueryTrimmed.length > 0) {
			where["$or"] = [];
			const parts = searchQueryTrimmed.split(/\s+/).filter((term) => term.length > 0);
			const phoneSearchDigits = searchQueryTrimmed.replace(/\D/g, "");

			if (parts.length > 1) {
				where["$or"] = [
					{
						$and: {
							firstname: { $ilike: `%${parts[0]}%` },
							lastname: { $ilike: `%${parts[1]}%` }
						}
					}
				];
			} else {
				where["$or"].push({
					firstname: { $ilike: `%${searchQueryTrimmed}%` }
				});
				where["$or"].push({
					lastname: { $ilike: `%${searchQueryTrimmed}%` }
				});
			}

			where["$or"].push({
				emails: {
					$ilike: `%${searchQueryTrimmed}%`
				}
			});

			where["$or"].push({
				phones: {
					$ilike: `%${searchQueryTrimmed}%`
				}
			});

			if (phoneSearchDigits.length > 0 && /^[+\d\s()[\].-]+$/.test(searchQueryTrimmed)) {
				where["$or"].push({
					phonesRaw: {
						$ilike: `%${phoneSearchDigits}%`
					}
				});
			}
		}

		// Trailing `id ASC` tiebreaker: none of the name/email/phone columns are unique, so
		// without it, ties across a page-boundary OFFSET can return the same contact on two
		// pages (collected below dedupes defensively) or skip a contact entirely.
		const order = [
			["firstname", "ASC NULLS LAST", ["empties.last", "ci"]],
			["lastname", "ASC NULLS LAST", ["empties.last", "ci"]],
			["emails", "ASC NULLS LAST", ["empties.last", "ci"]],
			["phones", "ASC NULLS LAST", ["empties.last", "ci"]],
			["id", "ASC"]
		];

		// Keyed by id (not a plain array) so a duplicate row from a page-boundary tie
		// collapses to one entry instead of colliding on FilterMenuList's `:key="item.id"`.
		const collected = new Map<string, Customer>();
		let page = 1;

		try {
			while (collected.size < MAX_CUSTOMERS) {
				const result: ResponseArray<Customer> = await api.getCustomerViews({ where, page, pageSize: PAGE_CHUNK, order });

				if (requestId !== activeSearchRequestId) return;

				const sizeBeforePage = collected.size;
				for (const customer of result.rows) {
					collected.set(customer.id, customer);
				}

				// An empty page can never converge (page += 1 forever), regardless of a stale
				// `count` — always stop here.
				if (result.rows.length === 0) break;
				// A non-empty page that added zero NEW ids (100% duplicates of already-collected
				// rows) can't converge either — the dedup Map means `collected.size` no longer
				// grows by `rows.length` every iteration the way a plain array did, so this guard
				// restores the old unconditional-growth termination guarantee: if a backend
				// pagination bug ever returns duplicate-content pages while `count` claims more
				// remain, stop rather than spin forever.
				if (collected.size === sizeBeforePage) {
					logError("CustomerFilter: loadCustomers pagination made no progress on a non-empty page", new Error("no-progress"), {
						page,
						collected: collected.size,
						count: result.count
					});
					break;
				}
				// A short (but non-empty) page is NOT reliably the last page in principle — trust
				// `count` over the page's row count whenever it's available, defensively (mirrors
				// UserFilter.vue's loadUsers, which needs this for real: its flat-query hydration
				// path has a genuine two-query id-then-hydrate step that can under-return one page.
				// customerViews.ts's getCustomerView is a single plain findAndCountAll with no such
				// step, so this branch is precautionary here, not a known-reachable gap.
				if (typeof result.count === "number") {
					if (collected.size >= result.count) break;
				} else if (result.rows.length < PAGE_CHUNK) {
					break;
				}
				page += 1;
			}

			if (requestId !== activeSearchRequestId) return;

			customers.value = Array.from(collected.values()).slice(0, MAX_CUSTOMERS);
			await setPreselectedCustomers(preSelectedCustomers);
		} catch (e: unknown) {
			// On mid-loop rejection, expose what we already collected so a transient page-N
			// failure doesn't blank the dropdown — and surface to Datadog so the failure is
			// distinguishable from an empty org. Guard against a stale request overwriting
			// a newer in-flight one; mirror the success path's setPreselectedCustomers so
			// the user's previously-selected contacts keep their checkbox state.
			if (requestId === activeSearchRequestId) {
				customers.value = Array.from(collected.values()).slice(0, MAX_CUSTOMERS);
				await setPreselectedCustomers(preSelectedCustomers);
			}
			logError("CustomerFilter: loadCustomers failed", e, { page, collected: collected.size });
		} finally {
			if (requestId === activeSearchRequestId) {
				loading.value = false;
			}
		}
	}

	const setPreselectedCustomers = async (preSelectedCustomers: string[]): Promise<Customer[]> => {
		try {
			if (preSelectedCustomers?.length > 0) {
				const customerIds = customers.value.map((c) => c.id);
				// only load the selected customers if they are not already in the list
				if (preSelectedCustomers.some((c) => !customerIds.includes(c))) {
					const preSelectedCustomersResult: ResponseArray<Customer> = await api.getCustomerViews({
						where: {
							id: { $in: preSelectedCustomers }
						}
					});
					const customerMap = new Map<string, Customer>();
					[...preSelectedCustomersResult.rows, ...customers.value].forEach((customer) => {
						customerMap.set(customer.id, customer);
					});
					customers.value = Array.from(customerMap.values());
				}
			}
		} catch (error) {
			logError("CustomerFilter: setPreselectedCustomers failed", error, { preSelectedCustomers: preSelectedCustomers?.length ?? 0 });
			return [];
		}

		// Filters.vue passes `customerIds: null` after the filter is cleared, and null
		// bypasses the `() => []` prop default — guard it here (LSP-16408).
		return (preSelectedCustomers ?? []).map((id) => customers.value.find((c) => c.id === id)).filter(Boolean) as Customer[];
	};

	// LSP-13672: the /customer-views payload carries flat `emails` / `phones` strings; the shared
	// Customer type declares `phones` but not `emails` (src/types/Customer.d.ts), and the wrapper
	// below has always read `emails` off it. Alias locally rather than widening the shared type in
	// a UI-only fix.
	type CustomerRow = Customer & { emails?: string };

	// Branch predicates for getCustomerViewDisplayName's fallback order (name > emails > phones).
	// Plain truthiness on purpose: these must pick the SAME branch the title picked, so no .trim().
	const hasName = (record: CustomerRow): boolean => Boolean(record.firstname || record.lastname);
	const hasEmails = (record: CustomerRow): boolean => Boolean(record.emails && record.emails.length > 0);
	const hasPhones = (record: CustomerRow): boolean => Boolean(record.phones && record.phones.length > 0);

	const getcustomerViewDisplayNameWrapper = (record: CustomerRow): string => {
		const raw = getCustomerViewDisplayName(record);
		// Only the phone-fallback branch (no name, no email) gets formatted for display.
		// getCustomerViewDisplayName itself must stay raw because it doubles as a
		// sort/filter key — formatting it would drift from the server's $ilike search
		// against the raw `phones` column.
		const isPhoneFallback = !hasName(record) && !hasEmails(record);
		if (isPhoneFallback && raw) {
			return formatPhones(raw, currentOrganizationDefaultCountry.value);
		}
		return raw;
	};

	/**
	 * LSP-13672 subtitle guards. Render a subtitle only when it would NOT repeat the title.
	 * The title falls back name > emails > phones, so:
	 *   - phone is redundant only in the phone-fallback branch (no name AND no email);
	 *   - email is redundant whenever there is no name, because then the title IS the email.
	 * These deliberately do NOT match ActionVideo.vue's guards — that list titles
	 * `name || phone || email`, i.e. the opposite phone/email precedence.
	 *
	 * Break-it rounds 1-6 hardened this to a deliberately simple, WHOLE-STRING comparison:
	 *   - a whitespace/comma/delimiter-only phones/emails string passes the `.length > 0`
	 *     presence check but has no real content, so it must not render an empty-looking
	 *     subtitle line — `hasVisibleEmailContent` below and formatPhones' own internal
	 *     split/trim/filter (it already discards blank comma-separated entries) both catch this;
	 *   - a contact whose name field is itself set to their own phone/email text (unusual data,
	 *     but not impossible) must not show a subtitle that repeats the title verbatim, even
	 *     though the branch logic above only tracks WHICH FIELD backs the title, not its text —
	 *     this is a plain `!==` comparison against the WHOLE formatted/raw string;
	 *   - a non-string phones/emails value (hostile/malformed JSON) must be treated as absent,
	 *     never passed to .trim() and crash.
	 * An earlier version of this guard tried to also filter an individually-colliding entry out
	 * of a comma-joined multi-value field (keeping the other entries visible). That required
	 * splitting on "," — which breaks on an RFC 5322 quoted local-part that legally contains a
	 * comma (e.g. `"Doe, Jane"@example.com`), and a quote-aware splitter still had a gap for an
	 * unbalanced/malformed quote (break-it rounds 4-6). Given how rare an exact per-entry title
	 * collision inside a multi-value field is in practice, the whole-string comparison is the
	 * deliberate trade-off: a multi-value field is only ever hidden/altered when the ENTIRE field
	 * matches the title, never partially — no comma splitting, no quoting logic, at all.
	 */
	const hasVisibleEmailContent = (email: string): boolean => /[^,\s]/.test(email);
	const getFormattedPhoneSubtitle = (record: CustomerRow): string => formatPhones(record.phones, currentOrganizationDefaultCountry.value);
	const getEmailSubtitleText = (record: CustomerRow): string => (typeof record.emails === "string" ? record.emails : "");
	const shouldShowPhoneSubtitle = (record: CustomerRow): boolean => {
		if (!hasPhones(record) || !(hasName(record) || hasEmails(record))) return false;
		const formatted = getFormattedPhoneSubtitle(record);
		return formatted.length > 0 && formatted !== getcustomerViewDisplayNameWrapper(record);
	};
	const shouldShowEmailSubtitle = (record: CustomerRow): boolean => {
		if (!hasEmails(record) || !hasName(record)) return false;
		const email = getEmailSubtitleText(record);
		return hasVisibleEmailContent(email) && email !== getcustomerViewDisplayNameWrapper(record);
	};

	function clearDebounceTimer(): void {
		if (searchDebounceTimer) {
			clearTimeout(searchDebounceTimer);
			searchDebounceTimer = null;
		}
	}

	// Watchers
	watch(searchQuery, () => {
		clearDebounceTimer();
		if (!isFilterOpen.value) return;
		searchDebounceTimer = setTimeout(() => {
			// Re-check at fire time, not just schedule time: closeMenu()'s searchQuery reset
			// is a no-op (no watcher fire) when the field was already empty, so a pending
			// timer scheduled while the menu was open can otherwise still fire after close
			// (LSP-16836 — same fix applied to UserFilter.vue's identical pattern).
			if (!isFilterOpen.value) return;
			loadCustomers();
		}, 300);
	});

	watch(
		() => props.preSelectedCustomers,
		async (newVal: string[], oldVal: string[] | undefined) => {
			if (!newVal || newVal.length == 0) {
				return;
			}

			if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
				const generationAtStart = userActionGeneration;
				const prefilled = await setPreselectedCustomers(newVal);
				if (prefillSuperseded(generationAtStart, "preSelectedCustomersWatcher")) {
					return;
				}
				selectedItems.value = prefilled;
				applyFilter(() => emit("filter-customer", appliedItems.value), false);
			}
		}
	);

	// Lifecycle
	onMounted(async () => {
		await onMount();
	});

	onBeforeUnmount(() => {
		clearDebounceTimer();
	});

	async function restore(): Promise<void> {
		if (props.preSelectedCustomers?.length > 0) {
			const generationAtStart = userActionGeneration;
			const prefilled = await setPreselectedCustomers(props.preSelectedCustomers);
			if (prefillSuperseded(generationAtStart, "restore")) {
				return;
			}
			selectedItems.value = prefilled;
			applyFilter(() => emit("filter-customer", appliedItems.value), false);
		}
	}

	defineExpose({
		clear: (emitEvent: boolean = false) => clearFromUser(emitEvent),
		restore,
		loadCustomers,
		isFilterOpen
	});
</script>

<style scoped>
	.max-w-150 {
		max-width: 150px;
	}

	.v-chip {
		padding: 4px 8px;
		height: 28px;
		font-size: 12px;
		margin-bottom: 4px;
	}

	.menu-content {
		width: 100%;
		max-width: 300px;
	}

	.menu-card {
		width: 100%;
		box-sizing: border-box;
	}

	.status-label-filter {
		width: 28px;
		height: 28px;
		font-size: 1rem;
		border-radius: 50%;
		background-color: #b1d8ff;
		color: #0070ff;
	}

	.status-label-chip {
		font-size: 12px;
		margin-bottom: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		box-sizing: border-box;
	}

	.customer-skeleton-row {
		background: transparent;
	}
</style>
